import cls from "classnames";
import { jsPDF } from "jspdf";
import moment from "moment";
import React from "react";
import logo from "../../../assets/logo-teurona.svg";
import {
  FONT_INTER_BOLD,
  FONT_INTER_NORMAL,
  FONT_INTER_REGULAR,
} from "../fontFiles";
import {
  drawImage,
  RATIO_FACTOR,
  splitText,
  TemplateProps,
} from "../Template1";
import styles from "./index.module.scss";
const QRCode = require("qrcode.react");

export const createPDF = (name: string, props: TemplateProps) => {
  const doc = new jsPDF({
    orientation: "l",
    unit: "px",
    format: "a4",
  });
  let canvas: HTMLCanvasElement | undefined = props.showQrCode
    ? (document.getElementById("certicate-qr") as HTMLCanvasElement)
    : undefined;

  doc.addFileToVFS("Inter.ttf", FONT_INTER_NORMAL);
  doc.addFileToVFS("InterRegular.ttf", FONT_INTER_REGULAR);
  doc.addFileToVFS("InterBold.ttf", FONT_INTER_BOLD);

  // draw body
  doc.setFillColor("#4376F9");
  doc.rect(0, 0, 74 * RATIO_FACTOR, doc.internal.pageSize.height, "F");

  // added fonts
  doc.addFont("Inter.ttf", "Inter", "normal");
  doc.addFont("InterRegular.ttf", "Inter", "regular");
  doc.addFont("InterBold.ttf", "Inter", "bold");

  // draw content
  doc.setFont("Inter", "normal");
  doc.setTextColor("#81858B");
  if (props.showNameAndLogo) {
    drawImage("businessLogo", doc, 126 * RATIO_FACTOR, 47 * RATIO_FACTOR);
  }
  if (props.showName) {
    doc.setFontSize(14);
    doc.text("Certificado del curso", 126 * RATIO_FACTOR, 145 * RATIO_FACTOR);
    doc.setFont("Inter", "bold");
    doc.setFontSize(28);
    doc.setTextColor("#4376F9");
    splitText(
      doc,
      props.courseName,
      659 * RATIO_FACTOR,
      26,
      126 * RATIO_FACTOR,
      181 * RATIO_FACTOR
    );
  }
  if (props.showTotalHours) {
    doc.setFont("Inter", "regular");
    doc.setFontSize(16);
    doc.setTextColor("#686F84");
    doc.text(
      `${props.totalHours} horas de estudio`,
      126 * RATIO_FACTOR,
      248 * RATIO_FACTOR
    );
  }
  doc.setFont("Inter", "normal");
  doc.setFontSize(14);
  doc.setTextColor("#81858B");
  if (props.showDescription) {
    splitText(
      doc,
      props.description,
      625 * RATIO_FACTOR,
      13,
      126 * RATIO_FACTOR,
      274 * RATIO_FACTOR
    );
  }
  if (props.showTraineeFullName || props.showTraineeNationalId) {
    doc.text("Otorgado a ", 126 * RATIO_FACTOR, 358 * RATIO_FACTOR);
  }
  doc.setTextColor("#686F84");
  if (props.showTraineeFullName) {
    doc.setFont("Inter", "bold");
    doc.setFontSize(22);
    doc.text(props.name, 126 * RATIO_FACTOR, 388 * RATIO_FACTOR);
  }
  if (props.showTraineeNationalId) {
    doc.setFont("Inter", "regular");
    doc.setFontSize(16);
    doc.text("ID. " + props.traineeNationalId, 126 * RATIO_FACTOR, 417 * RATIO_FACTOR);
  }

  // draw footer
  doc.setFont("Inter", "normal");
  doc.setFontSize(12);
  doc.setTextColor("#81858B");
  if (props.showCompanyName) {
    doc.text(props.companyName, 126 * RATIO_FACTOR, 539 * RATIO_FACTOR);
  }
  doc.text(
    moment(props.createdAt).format("DD/MM/YYYY"),
    354 * RATIO_FACTOR,
    539 * RATIO_FACTOR
  );
  if (props.showQrCode && canvas) {
    doc.addImage(
      canvas,
      "PNG",
      706 * RATIO_FACTOR,
      453 * RATIO_FACTOR,
      62 * RATIO_FACTOR,
      62 * RATIO_FACTOR
    );
  }
  if (props.showUniqueCode) {
    const txtWidth =
      (doc.getStringUnitWidth(props.uniqueCode) * doc.getFontSize()) /
      doc.internal.scaleFactor;
    doc.text(
      props.uniqueCode,
      778 * RATIO_FACTOR - txtWidth,
      539 * RATIO_FACTOR
    );
  }

  // Save document
  doc.save(`${name}.pdf`);
};

const Template3: React.FunctionComponent<TemplateProps> = ({
  showName,
  showDescription,
  showTotalHours,
  showTraineeFullName,
  showTraineeNationalId,
  showCompanyName,
  showNameAndLogo,
  showUniqueCode,
  showQrCode,
  name,
  courseName,
  description,
  totalHours,
  traineeNationalId,
  companyName,
  uniqueCode,
  createdAt,
  baseFontSizePx,
}) => (
  <article
    className={cls(styles.mainWrapper)}
    style={{ fontSize: `${baseFontSizePx}px` }}
  >
    <div className={styles.wrapper}>
      <section className={styles.container}>
        <header>
          <div className={styles.logo}>
            {showNameAndLogo && (
              <img
                src={logo}
                alt="businessLogo"
                id="businessLogo"
                crossOrigin="anonymous"
              />
            )}
          </div>
        </header>
        <div className={styles.content}>
          <div className={styles.courseTitle}>
            {showName && <h5>Certificado del curso</h5>}
          </div>
          <div className={styles.courseName}>
            {showName && <h1>{courseName}</h1>}
          </div>
          <div className={styles.courseHours}>
            {showTotalHours && <h4>{`${totalHours} horas de estudio`}</h4>}
          </div>
          <div className={styles.description}>
            {showDescription && <p>{description}</p>}
          </div>
          <div className={styles.traineeTitle}>
            {(showTraineeFullName || showTraineeNationalId) && (
              <span>Otorgado a</span>
            )}
          </div>
          <div className={styles.traineeName}>
            {showTraineeFullName && <h2>{name}</h2>}
          </div>
          <div className={styles.traineeId}>
            {showTraineeNationalId && <h3>ID. {traineeNationalId}</h3>}
          </div>
        </div>
        <footer>
          <div className={styles.companyName}>
            {showCompanyName && <span>{companyName}</span>}
          </div>
          <span className={styles.date}>
            {moment(createdAt).format("DD/MM/YYYY")}
          </span>
          <div className={styles.qrWrapper}>
            {showQrCode && (
              <QRCode
                value={window.location.href}
                id="certicate-qr"
                renderAs="canvas"
              />
            )}
            {showUniqueCode && <span>{uniqueCode}</span>}
          </div>
        </footer>
      </section>
    </div>
  </article>
);

export default Template3;
